import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { DeviceService } from '../../../services/device/device.service';
import { MyDevice } from 'src/app/data-models/device/my-device.model';
import { Status } from 'src/app/data-models/widgets/status.model';
import { CommonService } from 'src/app/services/common/common.service';
import { SystemUpdateService } from 'src/app/services/system-update/system-update.service';
import { TranslateService } from '@ngx-translate/core';
import { TimerService } from 'src/app/services/timer/timer.service';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { map, mergeMap } from 'rxjs/operators';
import { AdPolicyService } from 'src/app/services/ad-policy/ad-policy.service';
import { WarrantyService } from 'src/app/services/warranty/warranty.service';
import { FormatLocaleDatePipe } from 'src/app/pipe/format-locale-date/format-locale-date.pipe';
import { FeatureToggleService } from 'src/app/services/feature-toggle/feature-toggle.service';
import { LocalStorageKey } from 'src/app/enums/local-storage-key.enum';
import { SystemUpdatePresenterService } from 'src/app/presenters/user-cases/system-update/system-update-presenter';
@Component({
	selector: 'vtr-widget-device',
	templateUrl: './widget-device.component.html',
	styleUrls: ['./widget-device.component.scss'],
	providers: [TimerService],
})
export class WidgetDeviceComponent implements OnInit, OnDestroy {
	public myDevice: MyDevice;
	public deviceStatus: Status[] = [];
	// subtitle = 'My device status';
	virusImage = 'assets//Device_antivirus.png';
	@Input() linkTabIndex = 0;

	private readonly SYSTEM_UPDATE_AS_LINK = true;
	private readonly SYSTEM_UPDATE_ID = 'systemupdate';
	private readonly SYSTEM_UPDATE_IS_SYSTEM_LINK = false;
	private readonly SYSTEM_UPDATE_PATH = 'device/system-updates';

	constructor(
		public deviceService: DeviceService,
		private commonService: CommonService,
		private formatLocaleDate: FormatLocaleDatePipe,
		private systemUpdateService: SystemUpdateService,
		private translate: TranslateService,
		private dashboardService: DashboardService,
		private warrantyService: WarrantyService,
		private adPolicyService: AdPolicyService,
		private featureToggleService: FeatureToggleService,
		private systemUpdatePresenter: SystemUpdatePresenterService
	) {
		this.myDevice = new MyDevice();
	}

	ngOnInit() {
		this.initDeviceInfo();
		this.getDeviceInfo();
	}

	ngOnDestroy() {}

	private initDeviceInfo() {
		let hwInfoCache = this.commonService.getLocalStorageValue(
			LocalStorageKey.WidgetDeviceHardwareInfo,
			undefined
		);
		let index = 0;
		const systemStatus = this.deviceStatus;

		if (hwInfoCache === undefined) {
			const processor = new Status();
			processor.id = 'processor';
			this.translate.stream('device.myDevice.processor.notFound').subscribe((value) => {
				processor.title = value;
			});
			processor.path = 'ms-settings:about';
			processor.asLink = false;
			processor.isSystemLink = true;
			systemStatus[index++] = processor;

			const memory = new Status();
			memory.id = 'memory';
			this.translate.stream('device.myDevice.memory.notFound').subscribe((value) => {
				memory.title = value;
			});
			memory.path = 'ms-settings:about';
			memory.asLink = false;
			memory.isSystemLink = true;
			systemStatus[index++] = memory;

			const disk = new Status();
			disk.id = 'disk';
			this.translate.stream('device.myDevice.diskspace.notFound').subscribe((value) => {
				disk.title = value;
			});
			disk.path = 'ms-settings:storagesense';
			disk.asLink = false;
			disk.isSystemLink = true;
			systemStatus[index++] = disk;

			this.translate.stream('device.myDevice.learnMore').subscribe((value) => {
				processor.detail = value;
				memory.detail = value;
				disk.detail = value;
			});
		} else {
			systemStatus[index++] = hwInfoCache[0];
			systemStatus[index++] = hwInfoCache[1];
			systemStatus[index++] = hwInfoCache[2];
		}

		if (this.systemUpdatePresenter.isVisible()) {
			const systemUpdate = this.getSystemUpdateDefaultValue();
			systemStatus[index++] = systemUpdate;
		}
	}

	private getSystemUpdateDefaultValue() {
		const systemUpdate = new Status();
		systemUpdate.id = this.SYSTEM_UPDATE_ID;
		systemUpdate.title = this.translate.instant('device.myDevice.systemUpdate.notFound');
		systemUpdate.detail = this.translate.instant('device.myDevice.systemUpdate.title');

		systemUpdate.path = this.SYSTEM_UPDATE_PATH;
		systemUpdate.asLink = this.SYSTEM_UPDATE_AS_LINK;
		systemUpdate.isSystemLink = this.SYSTEM_UPDATE_IS_SYSTEM_LINK;
		return systemUpdate;
	}
	private getDeviceInfo() {
		// machineinfo
		this.deviceService.getMachineInfo().then((data) => {
			this.myDevice.family = data.family;
			this.myDevice.sn = data.serialnumber;
			this.myDevice.bios = data.biosVersion;
			this.myDevice.subBrand = data.subBrand;
			this.myDevice.productNo = data.mtm;
		});

		// processor memory disk
		this.deviceService.getHardwareInfo().then((data) => {
			if (data) {
				const processor = this.deviceStatus[0];
				processor.status = 0;
				this.translate.stream('device.myDevice.processor.title').subscribe((value) => {
					processor.title = value;
				});
				processor.systemDetails = `${data.processor.name}`;

				const memory = this.deviceStatus[1];
				const { total, used } = data.memory;
				let type = data.memory.type;
				this.translate.stream('device.myDevice.memory.title').subscribe((value) => {
					memory.title = value;
				});

				if (type.toLowerCase() === 'unknown') {
					type = '';
				}

				this.translate
					.stream('device.myDevice.of')
					.pipe(
						map((val) => {
							return `${this.commonService.formatBytes(
								used
							)} ${val} ${this.commonService.formatBytes(total)}`;
						})
					)
					.subscribe((value) => {
						memory.systemDetails = value;
					});

				if (used === total) {
					memory.status = 1;
				} else {
					memory.status = 0;
				}

				const disk = this.deviceStatus[2];
				const totalDisk = data.disk.total;
				const usedDisk = data.disk.used;
				this.translate.stream('device.myDevice.diskspace.title').subscribe((value) => {
					disk.title = value;
				});

				this.translate.stream('device.myDevice.of').subscribe((value) => {
					disk.systemDetails = `${this.commonService.formatBytes(
						usedDisk
					)} ${value} ${this.commonService.formatBytes(totalDisk)}`;
				});

				this.translate.stream('device.myDevice.learnMore').subscribe((value) => {
					processor.detail = value;
					memory.detail = value;
					disk.detail = value;
				});

				if (usedDisk === totalDisk) {
					disk.status = 1;
				} else {
					disk.status = 0;
				}
				this.commonService.setLocalStorageValue(
					LocalStorageKey.WidgetDeviceHardwareInfo,
					this.deviceStatus.slice(0, 3)
				);
			}
		});

		// sysupdate
		if (this.systemUpdatePresenter.isVisible()) {
			this.dashboardService.getRecentUpdateInfo().subscribe((data) => {
				if (data) {
					if (this.deviceStatus[3] === undefined) {
						this.deviceStatus[3] = this.getSystemUpdateDefaultValue();
					}

					const systemUpdate = this.deviceStatus[3];

					const updateStatus = data.status;
					const lastUpdate = data.lastupdate;
					const diffInDays = this.systemUpdateService.dateDiffInDays(lastUpdate);

					if (updateStatus === 1) {
						this.translate
							.stream('device.myDevice.systemUpdate.detail.uptoDate')
							.subscribe((value) => {
								systemUpdate.title = value;
							});
						// `Software up to date `;
						this.translate
							.stream('device.myDevice.systemUpdate.detail.updatedOn')
							.subscribe((value) => {
								systemUpdate.systemDetails = `${value} ${this.commonService.formatLocalDate(
									lastUpdate
								)}`;
							});

						if (diffInDays > 30) {
							this.translate
								.stream('device.myDevice.systemUpdate.detail.outdated')
								.subscribe((value) => {
									systemUpdate.title = value;
								});
							// `Software outdated `;
							systemUpdate.status = 1;
						} else {
							systemUpdate.status = 0;
						}
					} else {
						this.translate
							.stream('device.myDevice.systemUpdate.detail.outdated')
							.subscribe((value) => {
								systemUpdate.title = value;
							});

						this.translate
							.stream('device.myDevice.systemUpdate.detail.neverRanUpdate')
							.subscribe((value) => {
								systemUpdate.systemDetails = value;
							});
						// `never ran update`;
						systemUpdate.status = 1;
					}
					// always show green check icon
					systemUpdate.status = 0;
				}
			});
		}
	}
}
